import { debounce } from 'lodash'
import {
  ProjectViewEvent,
  ProjectViewEventData,
  ProjectViewEventType,
} from './project-view-event'

class ProjectView {
  private hiddenContainer: HTMLDivElement = document.createElement('div')

  private canvas: HTMLCanvasElement = document.createElement('canvas')

  private unityInstance?: UnityInstance

  private eventTarget: EventTarget = new EventTarget()

  private isLoaded = false

  private previousWindowWidth = 0

  private previousWindowHeight = 0

  /**
   * Initializes project view components.
   */
  public init() {
    this.setupUnityEventHandler()

    if (window.Cypress) {
      // Cypress has issues with WebGL, causing all tests to fail.
      return
    }

    const script: HTMLScriptElement = document.createElement('script')
    script.src = '/project-view/project-view.loader.js'
    script.async = true
    script.onload = () => {
      void this.createUnityInstance()
    }

    document.head.appendChild(script)
  }

  /**
   * Returns true if Unity is loaded.
   */
  public isReady(): boolean {
    return this.isLoaded
  }

  /**
   * Moves the Unity canvas from the hidden container into a visible element.
   */
  public mount(container: HTMLDivElement) {
    container.appendChild(this.canvas)
  }

  /**
   * Moves the Unity canvas from a visible element into the hidden container.
   */
  public unmount() {
    this.mount(this.hiddenContainer)
  }

  /**
   * Sends a message to Unity.
   */
  public sendEvent<T>(type: string, data: T) {
    if (!this.unityInstance) {
      throw new Error('Should initialize Unity before trying to send messages')
    }

    this.unityInstance.SendMessage(
      'ReactEventHandler',
      'HandleEventFromReact',
      JSON.stringify({ type, data: JSON.stringify(data) }),
    )
  }

  /**
   * Adds a Unity event listener.
   */
  public on(
    eventType: ProjectViewEventType,
    callback: (event: ProjectViewEvent) => void,
  ) {
    this.eventTarget.addEventListener(eventType, callback as EventListener)
  }

  /**
   * Removes a Unity event listener.
   */
  public off(
    eventType: ProjectViewEventType,
    callback: (event: ProjectViewEvent) => void,
  ) {
    this.eventTarget.removeEventListener(eventType, callback as EventListener)
  }

  /**
   * Creates required DOM elements, event handler and Unity instance.
   */
  private async createUnityInstance(): Promise<void> {
    this.hiddenContainer.setAttribute(
      'style',
      'position: absolute; visibility: hidden',
    )
    this.canvas.setAttribute('id', 'project-view-canvas')
    this.canvas.setAttribute(
      'style',
      'display: block; width: 100%; height: 100%',
    )

    this.hiddenContainer.appendChild(this.canvas)
    document.body.appendChild(this.hiddenContainer)

    this.unityInstance = await window.createUnityInstance(
      this.canvas,
      {
        frameworkUrl: '/project-view/project-view.framework.js',
        codeUrl: '/project-view/project-view.wasm',
        dataUrl: '/project-view/project-view.data',
      },
      () => {},
    )

    this.setupCanvasResizeHandler()

    this.isLoaded = true
    this.eventTarget.dispatchEvent(new ProjectViewEvent('loaded'))
  }

  private setupUnityEventHandler(): void {
    window.handleEventFromUnity = (json: string) => {
      this.eventTarget.dispatchEvent(
        new ProjectViewEvent(
          'unityEvent',
          JSON.parse(json) as ProjectViewEventData,
        ),
      )
    }
  }

  private setupCanvasResizeHandler(): void {
    this.previousWindowWidth = window.innerWidth
    this.previousWindowHeight = window.innerHeight

    window.addEventListener(
      'resize',
      debounce(() => {
        if (!this.unityInstance) {
          return
        }

        const widthDifference = window.innerWidth - this.previousWindowWidth
        const heightDifference = window.innerHeight - this.previousWindowHeight

        this.previousWindowWidth = window.innerWidth
        this.previousWindowHeight = window.innerHeight

        if (widthDifference < 0 || heightDifference > 0) {
          // Resizing seems to work automatically in these cases.
          return
        }

        // Force a resize by Unity.
        this.canvas.removeAttribute('width')
        this.canvas.removeAttribute('height')
      }, 50),
    )
  }
}

export const projectView = new ProjectView()
